import { ErrorMessage } from '@hookform/error-message';
import { ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';
import { GroupBase, OptionsOrGroups } from 'react-select';

import { Hint } from '../Hint';
import { WillowSelect } from './WillowSelect';

interface BaseProps<T extends FieldValues> {
  options: OptionsOrGroups<any, GroupBase<any>>;
  control: Control<T>;
  hideErrorMessage?: boolean;
  label?: ReactNode;
  optional?: boolean;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
  displayValue?: boolean;
  classNamePrefix?: string;
  maxMenuHeight?: number;
  menuPosition?: 'absolute' | 'fixed';
  hint?: ReactNode;
}

interface SingleProps<T extends FieldValues> extends BaseProps<T> {
  isMulti?: false;
  fieldName: FieldPathByValue<T, boolean | string | number | undefined | null>;
}

interface MultiProps<T extends FieldValues> extends BaseProps<T> {
  isMulti: true;
  fieldName: FieldPathByValue<T, string[]>;
}

export function ControlledSelect<T extends FieldValues, TIsMulti extends boolean>({
  options,
  control,
  fieldName,
  hideErrorMessage = false,
  label,
  optional,
  placeholder,
  required,
  disabled,
  isClearable = true,
  displayValue = false,
  classNamePrefix,
  maxMenuHeight,
  isMulti = false,
  menuPosition = 'absolute',
  hint,
}: TIsMulti extends true ? MultiProps<T> : SingleProps<T>) {
  const {
    formState: { errors },
  } = useController({ control, name: fieldName });

  const FLAT_OPTIONS = options.flatMap((o) => ('options' in o ? o.options : o)); // In case groups are present

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {optional && <span className="u-color-bark3 ps-1 fw-normal"> (optional)</span>}
          {required && <span className="u-color-red1 ps-1">*</span>}
        </Form.Label>
      )}

      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange, value } }) => {
          const currentValue = isMulti
            ? value != null
              ? FLAT_OPTIONS.filter((r) => value.includes(r.value))
              : []
            : value != null
            ? FLAT_OPTIONS.filter((r) => value === r.value)
            : null;

          return displayValue ? (
            <div className="u-fs-2">{currentValue?.map((o) => o.label).join(', ') ?? value}</div>
          ) : (
            <WillowSelect
              placeholder={placeholder}
              options={options}
              value={currentValue}
              isClearable={isClearable}
              isDisabled={disabled}
              isOptionDisabled={(option) => option.disabled}
              onChange={(e) => {
                if (isMulti) {
                  onChange(e?.map((o: any) => o.value) ?? []);
                } else {
                  onChange(e?.value ?? '');
                }
              }}
              classNamePrefix={classNamePrefix}
              maxMenuHeight={maxMenuHeight}
              isMulti={isMulti}
              menuPosition={menuPosition}
            />
          );
        }}
      />

      {hint && <Hint>{hint}</Hint>}

      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
